import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2, XCircle, Sparkles } from "lucide-react";
import { motion } from "framer-motion";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  const arSession = params.get("ar_session");
  const [state, setState] = useState("checking"); // checking | paid | error | ar_done

  const poll = useCallback(async (attempt) => {
    if (!sessionId) return setState("error");
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        await refresh();
        setState("paid");
        return;
      }
      if (["failed", "expired"].includes(data.payment_status)) return setState("error");
      if (attempt >= MAX_POLLS) return setState("error");
      setTimeout(() => poll(attempt + 1), 2000);
    } catch {
      if (attempt >= MAX_POLLS) return setState("error");
      setTimeout(() => poll(attempt + 1), 2000);
    }
  }, [sessionId, refresh]);

  const confirmCard = useCallback(async () => {
    try {
      await api.post("/auto-recharge/confirm", { session_id: arSession });
      await refresh();
      setState("ar_done");
    } catch {
      setState("error");
    }
  }, [arSession, refresh]);

  useEffect(() => {
    if (arSession) confirmCard();
    else poll(0);
  }, [arSession, confirmCard, poll]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md rounded-3xl border border-border bg-card p-10 text-center relative overflow-hidden">
        <div className="absolute -top-24 -right-24 h-48 w-48 rounded-full bg-brand-pink/15 blur-3xl" />
        {state === "checking" && (
          <div className="relative" data-testid="pay-checking">
            <Loader2 className="h-12 w-12 mx-auto text-brand-blue animate-spin mb-6" />
            <h1 className="font-heading text-2xl font-light">Conferma del pagamento...</h1>
            <p className="text-sm text-muted-foreground mt-2">Attendi qualche istante.</p>
          </div>
        )}
        {state === "paid" && (          <div className="relative" data-testid="pay-success">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-brand-pink/15 flex items-center justify-center mb-6">
              <CheckCircle2 className="h-8 w-8 text-brand-pink" />
            </div>
            <h1 className="font-heading text-3xl font-light tracking-tight">Tutto <span className="text-brand-pink">pronto</span>!</h1>
            <p className="text-sm text-muted-foreground mt-3 mb-8">
              Il pagamento è andato a buon fine e il tuo saldo di battute è stato aggiornato. Buona traduzione!
            </p>
            <Button data-testid="go-app-btn" onClick={() => nav("/app")}
              className="rounded-full bg-brand-blue hover:bg-brand-blue/90 text-white h-11 px-8 gap-2">
              <Sparkles className="h-4 w-4" /> Inizia a tradurre
            </Button>
          </div>
        )}
        {state === "ar_done" && (
          <div className="relative" data-testid="ar-success">
            <div className="mx-auto h-14 w-14 rounded-2xl bg-brand-blue/15 flex items-center justify-center mb-6">
              <CheckCircle2 className="h-8 w-8 text-brand-blue" />
            </div>
            <h1 className="font-heading text-3xl font-light tracking-tight">Carta <span className="text-brand-blue">salvata</span>!</h1>
            <p className="text-sm text-muted-foreground mt-3 mb-8">
              L'auto-ricarica è attiva: quando il saldo scenderà sotto la soglia, ricaricheremo automaticamente le battute.
            </p>
            <Button data-testid="ar-go-app-btn" onClick={() => nav("/app")}
              className="rounded-full bg-brand-blue hover:bg-brand-blue/90 text-white h-11 px-8 gap-2">
              <Sparkles className="h-4 w-4" /> Torna all'app
            </Button>
          </div>
        )}
        {state === "error" && (
          <div className="relative" data-testid="pay-error">
            <XCircle className="h-12 w-12 mx-auto text-muted-foreground mb-6" />
            <h1 className="font-heading text-2xl font-light">Verifica non completata</h1>
            <p className="text-sm text-muted-foreground mt-2 mb-8">
              Non siamo riusciti a confermare il pagamento in tempo. Se è andato a buon fine, il tuo piano si aggiornerà a breve.
            </p>
            <Link to="/pricing"><Button variant="outline" className="rounded-full">Torna ai piani</Button></Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}
